import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraEyebrow, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraNumberedNav } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AssistRail } from '../../../components/OfficeWorkspace/AssistRail';
import { OfficeWorkspace } from '../../../components/OfficeWorkspace/OfficeWorkspace';
import { SourcesRail } from '../../../components/OfficeWorkspace/SourcesRail';
import { toContainerRef, useResponseWorkspace } from '../../../components/OfficeWorkspace/useOfficeWorkspace';
import { rfiQuestionHref } from '../../../components/RecordShell/rfiNav';
import { useProject } from '../../../hooks/useProjects';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { useRfiQuestions } from '../rfiQuestionHooks';
import { useRfiPeople, useRfiSectionMutations } from '../rfiSectionHooks';
import { QUESTION_STATUS_LABEL, questionNavTone, rfiQuestionStatus } from './questionStatus';
import { RfiAnswerReviewActions, SendForReviewPopover } from './RfiAnswerReviewControls';
import { RfiQuestionHead } from './RfiQuestionHead';
import { useRfiRecord } from './RfiRecordContext';
import { TemplateChoiceDialog } from './TemplateChoiceDialog';
import { useRfiAnswerReview } from './useRfiAnswerReview';
import { useEnsureRfiAnswerSections, useEnsureRfiResponseDocument, useRfiAnswerSections } from './useRfiAnswerSections';
const SIDE_TABS = [
    'assist',
    'sources',
    'history',
    'comments'
];
function LegacyBanner({ routeId, message }) {
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            asChild: true,
            size: "sm",
            variant: "secondary",
            children: /*#__PURE__*/ _jsx(Link, {
                to: `/rfi-response/${routeId}/legacy`,
                children: "Open the per-question editor"
            })
        }),
        "data-testid": Testids.rfiWorkspace.legacyBanner,
        tone: "info",
        children: message
    });
}
/**
 * The RFI response document (WRT-05): `questions/:sectionId` opens it at that
 * question's heading; `document` opens it at the top. One ONLYOFFICE document
 * per RFI, one anchored Heading 2 per government question.
 */ export function RfiQuestionPage({ mode }) {
    const record = useRfiRecord();
    const { sectionId = '' } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const side = params.get('side');
    const pangaeaId = record?.pangaeaId ?? '';
    const container = {
        kind: 'rfi',
        rfiResponseProjectId: pangaeaId
    };
    const workspace = useResponseWorkspace(container, undefined);
    const caps = workspace.data?.capabilities;
    const canEdit = caps?.canEdit ?? false;
    const writingOn = caps?.writingOn ?? false;
    const [template, setTemplate] = useState('');
    const ensure = useEnsureRfiResponseDocument(pangaeaId, !!record && canEdit && writingOn, template);
    const sections = useRfiAnswerSections(pangaeaId);
    const questions = useRfiQuestions(pangaeaId);
    const people = useRfiPeople();
    const { update } = useRfiSectionMutations(pangaeaId);
    const putBack = useEnsureRfiAnswerSections(pangaeaId);
    const review = useRfiAnswerReview(pangaeaId);
    const project = useProject(record?.zouProjectId ?? '');
    const [reviewAnchor, setReviewAnchor] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const list = sections.sections;
    const active = mode === 'question' ? list.find((s)=>s.id === sectionId) : undefined;
    const n = active ? list.indexOf(active) + 1 : 0;
    const source = useMemo(()=>{
        if (!active) return undefined;
        const q = questions.data?.questions.find((x)=>x.sectionIds.includes(active.id));
        return q?.source ? {
            originalText: q.source.originalText,
            documentReference: q.source.documentReference,
            section: q.source.section
        } : undefined;
    }, [
        active,
        questions.data
    ]);
    if (!record || workspace.isLoading || sections.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraSpinner, {
            className: "m-[var(--space-6)]",
            label: "Loading the RFI"
        });
    }
    if (mode === 'question' && !active) {
        return /*#__PURE__*/ _jsx(Navigate, {
            replace: true,
            to: `/rfi-response/${record.routeId}/questions`
        });
    }
    const legacyError = ensure.error instanceof ConnectError && ensure.error.code === Code.FailedPrecondition ? ensure.error.rawMessage : null;
    if (legacyError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-[var(--space-4)]",
            children: /*#__PURE__*/ _jsx(LegacyBanner, {
                message: legacyError,
                routeId: record.routeId
            })
        });
    }
    if (ensure.data?.needsTemplateChoice) {
        return /*#__PURE__*/ _jsx(TemplateChoiceDialog, {
            onChoose: setTemplate,
            options: ensure.data.templateOptions.map((o)=>({
                    attachmentId: o.attachmentId,
                    filename: o.filename
                }))
        });
    }
    if (ensure.isFetching && !ensure.data) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-[var(--space-2)] p-[var(--space-6)]",
            "data-testid": Testids.rfiWorkspace.preparingDocument,
            children: [
                /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: false
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-ui",
                    tone: "muted",
                    children: "Preparing the response document…"
                })
            ]
        });
    }
    const documentId = ensure.data?.documentId || workspace.data?.activeDocumentId || '';
    if (!documentId) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            className: "m-[var(--space-6)]",
            description: ensure.isError ? "The response document couldn't be prepared. Try again in a moment." : 'Nobody has started the response document for this RFI yet.',
            framed: true,
            title: "No response document yet"
        });
    }
    const nav = /*#__PURE__*/ _jsx(SpecraNumberedNav, {
        activeId: active?.id,
        "data-testid": Testids.rfiWorkspace.questionNav,
        getItemTestId: ()=>Testids.rfiWorkspace.questionNavItem,
        items: list.map((s, i)=>({
                id: s.id,
                label: String(i + 1),
                title: `Question ${i + 1}: ${QUESTION_STATUS_LABEL[rfiQuestionStatus(s)]}`,
                tone: questionNavTone(s),
                href: rfiQuestionHref(record.routeId, s.id)
            })),
        label: "Questions",
        leadingLabel: "Questions",
        renderLink: renderRouterLink
    });
    const header = active ? /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)]",
        children: [
            /*#__PURE__*/ _jsx(RfiQuestionHead, {
                active: active,
                canEdit: canEdit,
                onAssign: (patch)=>update.mutate({
                        sectionId: active.id,
                        ...patch
                    }),
                people: people.data ?? [],
                routeId: record.routeId,
                sections: list,
                source: source
            }),
            active.orphanedAt ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                action: canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.rfiWorkspace.putHeadingBack,
                    isLoading: putBack.isPending,
                    onClick: ()=>putBack.mutate(),
                    size: "sm",
                    variant: "secondary",
                    children: "Put it back"
                }) : undefined,
                "data-testid": Testids.rfiWorkspace.orphanBanner,
                tone: "danger",
                children: `The heading for Question ${n} is missing from the document.`
            }) : null,
            /*#__PURE__*/ _jsx(RfiAnswerReviewActions, {
                active: active,
                canApprove: caps?.canApprove ?? false,
                compareHref: `${rfiQuestionHref(record.routeId, active.id)}?version=${active.finalVersionId}`,
                error: errorText(review.approve.error ?? review.requestChanges.error),
                isApproving: review.approve.isPending,
                isRequesting: review.requestChanges.isPending,
                onApprove: ()=>review.approve.mutate({
                        sectionId: active.id
                    }),
                onRequestChanges: (note)=>review.requestChanges.mutate({
                        sectionId: active.id,
                        note
                    })
            })
        ]
    }) : /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        children: [
            list.length > 0 ? nav : null,
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                children: "Response document"
            })
        ]
    });
    const status = active ? rfiQuestionStatus(active) : 'not_started';
    const inReview = status === 'in_review';
    const isFinal = status === 'final';
    const team = (project.data?.members ?? []).map((m)=>({
            userId: m.userId,
            name: m.name,
            email: m.email
        }));
    return /*#__PURE__*/ _jsxs("div", {
        className: "h-full",
        "data-testid": Testids.rfiWorkspace.shell,
        children: [
            /*#__PURE__*/ _jsx(OfficeWorkspace, {
                anchor: active?.anchorBookmark || undefined,
                container: container,
                cta: active && canEdit ? {
                    label: inReview ? 'In review' : isFinal ? 'Final' : 'Send for review',
                    onClick: (anchor)=>{
                        setReviewAnchor(anchor ?? null);
                        setReviewOpen(true);
                    },
                    disabled: inReview || isFinal || review.requestReview.isPending,
                    disabledReason: inReview ? 'This answer is waiting for review.' : isFinal ? 'This answer is approved.' : undefined
                } : null,
                documentId: documentId,
                header: header,
                hiddenSideTabs: writingOn ? undefined : [
                    'sources'
                ],
                onJumpToAnchor: (anchor)=>{
                    const target = list.find((s)=>s.anchorBookmark === anchor);
                    if (target) navigate(rfiQuestionHref(record.routeId, target.id));
                },
                initialSideTab: side && SIDE_TABS.includes(side) ? side : undefined,
                sideTabs: writingOn ? {
                    assist: ({ activeDocumentId })=>/*#__PURE__*/ _jsx(AssistRail, {
                            canEdit: canEdit,
                            documentId: activeDocumentId,
                            projectId: record.zouProjectId || undefined,
                            responseContainer: toContainerRef(container),
                            rfiResponseProjectId: pangaeaId,
                            sectionAnchor: active?.anchorBookmark || undefined,
                            surface: "rfi_response",
                            unitNoun: "answer"
                        }),
                    sources: ({ activeDocumentId, currentVersionId, remount })=>/*#__PURE__*/ _jsx(SourcesRail, {
                            baseVersionId: currentVersionId,
                            canWrite: canEdit,
                            container: container,
                            documentId: activeDocumentId,
                            onInserted: (versionId)=>void remount(versionId),
                            onJumpToAnchor: (anchor)=>{
                                const target = list.find((s)=>s.anchorBookmark === anchor);
                                if (target) navigate(rfiQuestionHref(record.routeId, target.id));
                            },
                            sectionAnchor: active?.anchorBookmark || undefined,
                            unitNoun: "answer"
                        })
                } : undefined,
                unitNoun: "answer"
            }),
            active && reviewOpen ? /*#__PURE__*/ _jsx(SendForReviewPopover, {
                anchor: reviewAnchor,
                defaultReviewerId: active.reviewerUserId,
                error: errorText(review.requestReview.error),
                isSending: review.requestReview.isPending,
                onOpenChange: setReviewOpen,
                onSend: (a)=>review.requestReview.mutate({
                        sectionId: active.id,
                        ...a
                    }, {
                        onSuccess: ()=>setReviewOpen(false)
                    }),
                open: reviewOpen,
                team: team
            }) : null
        ]
    });
}
function errorText(err) {
    if (!err) return undefined;
    if (err instanceof ConnectError) return err.rawMessage;
    return err instanceof Error ? err.message : 'Something went wrong.';
}
